<template>
  <div class="card">
    <div class="card-header py-2 d-flex flex-wrap align-items-center gap-2">
      <strong class="me-auto">Odsotnosti</strong>
      <MonthPicker v-model="month" />
    </div>
    <div class="card-body py-2">
      <AbsenceModePicker v-if="canEdit" v-model="mode" />

      <table class="table table-sm table-bordered text-center mb-2 absence-calendar">
        <thead>
          <tr>
            <th v-for="d in ['pon', 'tor', 'sre', 'čet', 'pet', 'sob', 'ned']" :key="d" class="small">{{ d }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(week, i) in weeks" :key="i">
            <td
              v-for="day in week"
              :key="day.date.format('YYYY-MM-DD')"
              :class="dayClass(day)"
              :title="absences[day.key] ? typeText(absences[day.key]) : ''"
              @click="clickDay(day, $event)"
            >
              {{ day.date.date() }}
              <small v-if="absences[day.key]" class="d-block fw-bold">{{ typeShort(absences[day.key]) }}</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue"
import dayjs from "dayjs"
import { ABSENCE_MODES, datesBetween, isWeekend, toDay, useAbsences } from "../composition-api/useAbsences"
import AbsenceModePicker from "./AbsenceModePicker.vue"
import MonthPicker from "./MonthPicker.vue"

defineOptions({ name: "AbsenceCalendar" })

const props = defineProps({
  workerId: { type: String, required: true },
})
const emit = defineEmits(["absent-changed"])

const { loadAbsencesAsync, setAbsencesAsync } = useAbsences()

const month = ref(dayjs().startOf("month"))
const mode = ref("Vacation")
const absences = ref({}) // { "YYYY-MM-DD": "Vacation" | "SickLeave" } for the whole year of `month`
const loadedYear = ref(null)
const lockDate = ref(null)
const today = ref(null)
const canEdit = ref(false)
const anchor = ref(null) // last clicked day, start of a shift-click range

const typeText = (type) => ABSENCE_MODES.find((m) => m.value === type)?.text ?? type
const typeShort = (type) => ABSENCE_MODES.find((m) => m.value === type)?.short ?? "?"

const weeks = computed(() => {
  const first = month.value
  const start = first.subtract((first.day() + 6) % 7, "day") // Monday on or before the 1st
  const result = []
  for (let d = start; d.isBefore(first.add(1, "month")) || result.at(-1)?.length < 7; d = d.add(1, "day")) {
    if (!result.length || result.at(-1).length === 7) result.push([])
    result.at(-1).push({ date: d, key: d.format("YYYY-MM-DD"), inMonth: d.month() === first.month() })
  }
  return result
})

const isLocked = (date) => !canEdit.value || (lockDate.value && date.isBefore(lockDate.value, "day"))

const dayClass = (day) => ({
  "text-muted": !day.inMonth,
  weekend: isWeekend(day.date),
  vacation: absences.value[day.key] === "Vacation",
  sick: absences.value[day.key] === "SickLeave",
  locked: canEdit.value && isLocked(day.date),
  editable: !isLocked(day.date),
  today: today.value && day.date.isSame(today.value, "day"),
})

const loadAsync = async () => {
  const year = month.value.year()
  const raw = await loadAbsencesAsync({
    from: dayjs(`${year}-01-01`),
    to: dayjs(`${year}-12-31`),
    workerId: props.workerId,
  })
  if (!raw) return
  loadedYear.value = year
  today.value = toDay(raw.today)
  lockDate.value = toDay(raw.lockDate)
  canEdit.value = raw.workers?.[0]?.canEdit === true
  absences.value = Object.fromEntries(raw.result.map((a) => [a.date, a.type]))
}

const clickDay = async (day, event) => {
  if (isLocked(day.date)) return
  const range = event.shiftKey && anchor.value ? datesBetween(anchor.value, day.date) : [day.date]
  anchor.value = day.date
  const wanted = mode.value === "Present" ? undefined : mode.value
  const dates = range.filter((d) => !isLocked(d) && absences.value[d.format("YYYY-MM-DD")] !== wanted)
  if (!dates.length) return

  const raw = await setAbsencesAsync(props.workerId, dates, mode.value)
  if (!raw) return
  const updated = { ...absences.value }
  for (const d of dates) {
    // only the loaded year is kept, the rest is reloaded when navigating there
    if (d.year() !== loadedYear.value) continue
    if (wanted) updated[d.format("YYYY-MM-DD")] = wanted
    else delete updated[d.format("YYYY-MM-DD")]
  }
  absences.value = updated
  if (raw.isAbsent !== null && raw.isAbsent !== undefined) {
    emit("absent-changed", raw.isAbsent)
  }
}

watch(month, async (value) => {
  if (value.year() !== loadedYear.value) await loadAsync()
})
watch(
  () => props.workerId,
  async () => {
    anchor.value = null
    await loadAsync()
  },
)

onMounted(loadAsync)
</script>

<style scoped>
.absence-calendar td {
  width: 14.28%;
  padding: 0.35rem 0;
  user-select: none;
}
.absence-calendar td.weekend {
  background: var(--bs-tertiary-bg);
}
.absence-calendar td.vacation {
  background: var(--bs-primary-bg-subtle);
}
.absence-calendar td.sick {
  background: var(--bs-warning-bg-subtle);
}
.absence-calendar td.editable {
  cursor: pointer;
}
.absence-calendar td.editable:hover {
  outline: 2px solid var(--bs-primary);
  outline-offset: -2px;
}
.absence-calendar td.locked {
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 4px,
    rgba(127, 127, 127, 0.15) 4px,
    rgba(127, 127, 127, 0.15) 8px
  );
}
.absence-calendar td.today {
  font-weight: bold;
  box-shadow: inset 0 0 0 2px var(--bs-success);
}
</style>
